import {
  CSSProperties,
  KeyboardEvent as ReactKeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Ripple } from "primereact/ripple";
import { useAuthenticator } from "@aws-amplify/ui-react";
import Cookies from "js-cookie";
import { CometChatUIKit } from "@cometchat/chat-uikit-react";
import { useGetBoMeQuery } from "../../store/services/api";
import { useIsFeatureFlagEnabled } from "../../hooks/useIsFeatureFlagEnabled";
import { useTenantScope } from "../../hooks/useTenantScope";
import { NAV_MODEL, NavGroup, NavItem } from "../navModel";
import {
  VisibleNavGroup,
  filterNavGroups,
  flattenNavTargets,
  highlightSegments,
  toSearchTokens,
} from "../navSearch";
import { SidebarSearch } from "./SidebarSearch";
import { ACTIVE_CLASS, ROW_CLASSES } from "./sidebarRowClasses";
import { FeatureFlagKey } from "../../models/featureFlag";

/**
 * Sidebar del backoffice — SYSTEM_DESIGN.md §4 / INC-07.
 *
 * Drawer izquierdo persistente y colapsable (232px / 68px). Antes era un
 * <Sidebar position="right"> overlay, cerrado por defecto, que se abría con la
 * hamburguesa del header.
 *
 * La estructura del menú vive en navModel.ts, el filtrado del buscador en
 * navSearch.ts; acá solo está el renderer.
 */

const COLLAPSED_KEY = "grups.bo.sidebarCollapsed";

/** Fila señalada con las flechas mientras se busca (no es la ruta activa). */
const CURSOR_CLASS = "app-sidebar__row--cursor";

interface SidebarNavProps {
  collapsed: boolean;
  onToggle: () => void;
}

/** Icono opcional de un ítem. Decorativo: el label ya nombra el destino. */
const SidebarItemIcon = ({ icon }: { icon: NavItem["icon"] }) =>
  icon ? <i className={icon} aria-hidden="true" /> : null;

export const SidebarNav = ({ collapsed, onToggle }: SidebarNavProps) => {
  const { signOut } = useAuthenticator();
  const location = useLocation();
  const navigate = useNavigate();
  // MT-017 — el ABM de usuarios de BO es exclusivo de SUPER_ADMIN.
  const { data: me } = useGetBoMeQuery();
  const isSuperAdmin = me?.role === "SUPER_ADMIN";

  const scope = useTenantScope();
  const isAllTenantsScope =
    !scope.loading && scope.role === "SUPER_ADMIN" && scope.tenantId === null;
  const attendanceSectionFlag = useIsFeatureFlagEnabled("attendance_bo_section_enabled");
  const attendanceSectionVisible =
    isAllTenantsScope || (!attendanceSectionFlag.loading && attendanceSectionFlag.enabled);
  const pointsSectionFlag = useIsFeatureFlagEnabled("gamification_points_enabled");
  const pointsSectionVisible =
    isAllTenantsScope || (!pointsSectionFlag.loading && pointsSectionFlag.enabled);

  const isFlagVisible = (flagKey?: FeatureFlagKey): boolean => {
    if (flagKey === "attendance_bo_section_enabled") return attendanceSectionVisible;
    if (flagKey === "gamification_points_enabled") return pointsSectionVisible;
    return true;
  };

  /**
   * El menú que el usuario tiene derecho a ver, con los ítems ya filtrados por
   * rol y por flags. Todo lo demás (acordeón, búsqueda) trabaja sobre esto: el
   * buscador no puede ser una puerta trasera a lo que está oculto.
   */
  const visibleModel: VisibleNavGroup[] = NAV_MODEL.filter(
    (group) => (!group.superAdminOnly || isSuperAdmin) && isFlagVisible(group.featureFlagKey)
  ).map((group) => ({
    group,
    items: (group.items ?? []).filter(
      (item) => (!item.superAdminOnly || isSuperAdmin) && isFlagVisible(item.featureFlagKey)
    ),
  }));

  // ── Búsqueda ────────────────────────────────────────────────────────────
  const [query, setQuery] = useState("");
  const tokens = useMemo(() => toSearchTokens(query), [query]);
  const searching = tokens.length > 0;

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  /** Pedido de foco que quedó pendiente porque había que expandir primero. */
  const focusPending = useRef(false);

  const results = filterNavGroups(visibleModel, tokens);
  const targets = searching ? flattenNavTargets(results) : [];
  const targetIndex = new Map(targets.map((target, index) => [target.to, index]));

  /** Fila señalada con las flechas. Se acota por si la lista se achicó. */
  const [cursor, setCursor] = useState(0);
  const cursorIndex = Math.min(cursor, targets.length - 1);

  useEffect(() => {
    setCursor(0);
  }, [query]);

  const focusSearch = useCallback(() => {
    if (collapsed) {
      // El campo no está en el DOM mientras el menú está colapsado: se expande
      // y el foco se aplica en el efecto de abajo, ya montado.
      focusPending.current = true;
      onToggle();
      return;
    }
    inputRef.current?.focus();
  }, [collapsed, onToggle]);

  useEffect(() => {
    if (collapsed) {
      // Colapsado no se ve el campo: sostener la búsqueda dejaría una lista
      // filtrada de íconos sueltos sin forma de saber por qué está filtrada.
      setQuery("");
      return;
    }
    if (focusPending.current) {
      focusPending.current = false;
      inputRef.current?.focus();
    }
  }, [collapsed]);

  // Atajo global: Ctrl/⌘ + K enfoca el buscador desde cualquier pantalla.
  useEffect(() => {
    const handleShortcut = (event: KeyboardEvent) => {
      if (event.key?.toLowerCase() !== "k" || !(event.metaKey || event.ctrlKey)) return;
      event.preventDefault();
      focusSearch();
    };
    window.addEventListener("keydown", handleShortcut);
    return () => window.removeEventListener("keydown", handleShortcut);
  }, [focusSearch]);

  // Con listas largas el cursor puede caer fuera de la vista del sidebar.
  useEffect(() => {
    if (!searching) return;
    const row = listRef.current?.querySelector<HTMLElement>(`[data-nav-index="${cursorIndex}"]`);
    // jsdom no implementa scrollIntoView.
    if (row && typeof row.scrollIntoView === "function") row.scrollIntoView({ block: "nearest" });
  }, [cursorIndex, searching, query]);

  const clearSearch = () => {
    setQuery("");
    inputRef.current?.focus();
  };

  const handleSearchKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      clearSearch();
      return;
    }
    if (targets.length === 0) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setCursor((prev) => (Math.min(prev, targets.length - 1) + 1) % targets.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setCursor((prev) => (Math.min(prev, targets.length - 1) + targets.length - 1) % targets.length);
    } else if (event.key === "Enter") {
      event.preventDefault();
      const target = targets[cursorIndex];
      if (!target) return;
      navigate(target.to);
      setQuery("");
    }
  };

  // ── Acordeón ────────────────────────────────────────────────────────────
  /** Grupo cuyo acordeón está abierto. Arranca en el que contiene la ruta actual. */
  const groupOfCurrentRoute = useCallback(
    () =>
      visibleModel.find(({ items }) => items.some((i) => location.pathname.startsWith(i.to)))?.group
        .label ?? null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location.pathname, isSuperAdmin, attendanceSectionVisible, pointsSectionVisible]
  );

  const [openGroup, setOpenGroup] = useState<string | null>(groupOfCurrentRoute);

  /**
   * Grupo que contiene la ruta actual. Se resalta aunque esté colapsado: sin
   * esto, con el acordeón cerrado no hay forma de saber en qué módulo estás,
   * que es justo el criterio de aceptación de BO-NAV-02.
   */
  const activeGroup = groupOfCurrentRoute();

  // Al navegar, abrir el grupo que contiene la ruta destino.
  useEffect(() => {
    const g = groupOfCurrentRoute();
    if (g) setOpenGroup(g);
  }, [groupOfCurrentRoute]);

  const handleGroupClick = (group: NavGroup) => {
    // Colapsado no hay lugar para el submenú: expandir y abrir el grupo.
    if (collapsed) {
      onToggle();
      setOpenGroup(group.label);
      return;
    }
    setOpenGroup((prev) => (prev === group.label ? null : group.label));
  };

  const handleSignOut = async () => {
    try {
      Cookies.remove("accessToken");
      Cookies.remove("idToken");
      await CometChatUIKit.logout();
    } catch (error) {
      console.error("Error al cerrar sesión:", error);
      Cookies.remove("accessToken");
      Cookies.remove("idToken");
    } finally {
      signOut();
    }
  };

  /** Label con el tramo buscado resaltado. Sin búsqueda es el texto pelado. */
  const renderLabel = (label: string, className: string) => (
    <span className={className}>
      {highlightSegments(label, tokens).map((segment, index) =>
        segment.match ? (
          <mark key={index} className="app-sidebar__mark">
            {segment.text}
          </mark>
        ) : (
          <span key={index}>{segment.text}</span>
        )
      )}
    </span>
  );

  /** Fila de resultado: navega, limpia la búsqueda y deja el menú como estaba. */
  const renderResultLink = (to: string, label: string, icon?: NavItem["icon"], exact = true) => {
    const index = targetIndex.get(to) ?? -1;
    return (
      <NavLink
        to={to}
        end={exact}
        data-nav-index={index}
        onClick={() => setQuery("")}
        className={({ isActive }) =>
          `${ROW_CLASSES} ${isActive ? ACTIVE_CLASS : ""} ${
            index === cursorIndex ? CURSOR_CLASS : ""
          }`
        }
      >
        <SidebarItemIcon icon={icon} />
        {renderLabel(label, "app-sidebar__label font-medium")}
        <Ripple />
      </NavLink>
    );
  };

  return (
    <nav
      className={`app-sidebar surface-section border-right-1 surface-border select-none ${
        collapsed ? "app-sidebar--collapsed" : ""
      }`}
      aria-label="Navegación principal"
    >
      {/* §4.1 — el control de colapso va en la parte superior del menú. Toda la
          fila es el botón, como en el mockup: un target más grande que el ícono
          suelto. El título "Menú" se fue: con el header cruzando todo el ancho
          justo arriba, un encabezado de 24px competía con la barra de marca. */}
      <button
        type="button"
        onClick={onToggle}
        className="app-sidebar__toggle p-ripple flex align-items-center w-full border-none border-bottom-1 surface-border bg-transparent cursor-pointer text-600"
        style={{ padding: "14px 18px", gap: "10px" }}
        aria-label={collapsed ? "Expandir menú" : "Colapsar menú"}
        aria-expanded={!collapsed}
        title={collapsed ? "Expandir menú" : "Colapsar menú"}
      >
        <i className="app-sidebar__toggle-icon pi pi-chevron-left" />
        {/* <span className="app-sidebar__label text-xs font-bold">Colapsar menú</span> */}
        <Ripple />
      </button>

      <SidebarSearch
        ref={inputRef}
        value={query}
        collapsed={collapsed}
        resultCount={searching ? targets.length : null}
        onChange={setQuery}
        onKeyDown={handleSearchKeyDown}
        onClear={clearSearch}
        onExpand={focusSearch}
      />

      <ul ref={listRef} className="list-none p-2 m-0 flex-1 overflow-y-auto">
        {/* Buscando, los grupos dejan de ser acordeones y pasan a ser el
            encabezado del resultado: qué módulo es cada destino. Plegarlos no
            aportaría nada y el header clickeable sin efecto confunde. */}
        {searching
          ? results.map(({ group, items }, groupIndex) => (
              <li
                // Prefijo distinto al del acordeón a propósito: entrar o salir
                // de la búsqueda remonta las filas y la animación de entrada
                // corre. Entre tecla y tecla la key no cambia, así que el grupo
                // que sobrevive se queda quieto en vez de parpadear.
                key={`result-${group.label}`}
                className="app-sidebar__result"
                style={{ "--nav-i": groupIndex } as CSSProperties}
              >
                {group.to ? (
                  renderResultLink(group.to, group.label, group.icon, false)
                ) : (
                  <>
                    <div className="app-sidebar__result-group">
                      <i className={group.icon} />
                      {renderLabel(group.label, "app-sidebar__result-label")}
                    </div>
                    <ul className="app-sidebar__submenu list-none py-0 pl-3 pr-0 m-0">
                      {items.map((item) => (
                        <li key={item.to}>{renderResultLink(item.to, item.label, item.icon)}</li>
                      ))}
                    </ul>
                  </>
                )}
              </li>
            ))
          : visibleModel.map(({ group, items }) => {
              // Enlace directo, sin acordeón.
              if (group.to) {
                return (
                  <li key={group.label}>
                    <NavLink
                      to={group.to}
                      className={({ isActive }) =>
                        `${ROW_CLASSES} ${isActive ? ACTIVE_CLASS : ""}`
                      }
                      title={collapsed ? group.label : undefined}
                    >
                      <i className={group.icon} />
                      <span className="app-sidebar__label font-medium">{group.label}</span>
                      <Ripple />
                    </NavLink>
                  </li>
                );
              }

              const isOpen = openGroup === group.label && !collapsed;

              return (
                <li key={group.label}>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => handleGroupClick(group)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleGroupClick(group);
                      }
                    }}
                    className={`${ROW_CLASSES} ${
                      activeGroup === group.label ? ACTIVE_CLASS : ""
                    }`}
                    aria-expanded={isOpen}
                    aria-current={activeGroup === group.label ? "true" : undefined}
                    title={collapsed ? group.label : undefined}
                  >
                    <i className={group.icon} />
                    <span className="app-sidebar__label font-medium">{group.label}</span>
                    <i
                      className={`app-sidebar__chevron app-sidebar__group-chevron pi pi-chevron-down ml-auto mr-1 ${
                        isOpen ? "app-sidebar__group-chevron--open" : ""
                      }`}
                    />
                    <Ripple />
                  </div>

                  {isOpen && (
                    <ul className="app-sidebar__submenu list-none py-0 pl-3 pr-0 m-0">
                      {items.map((item) => (
                        <li key={item.to}>
                          <NavLink
                            to={item.to}
                            end
                            className={({ isActive }) =>
                              `${ROW_CLASSES} ${isActive ? ACTIVE_CLASS : ""}`
                            }
                          >
                            <SidebarItemIcon icon={item.icon} />
                            <span className="app-sidebar__label font-medium">{item.label}</span>
                            <Ripple />
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}

        {/* §6.5 pide "No hay [entidad] todavía" para los listados vacíos; una
            búsqueda sin coincidencias no es un listado vacío, así que dice qué
            se buscó y ofrece la salida. */}
        {searching && results.length === 0 && (
          <li className="app-sidebar__empty">
            <i className="pi pi-search" />
            <p className="app-sidebar__empty-text">No hay resultados para «{query.trim()}»</p>
          </li>
        )}
      </ul>

      <div className="flex-shrink-0">
        <hr className="mb-2 mx-3 border-top-1 border-none surface-border" />
        <button
          type="button"
          onClick={handleSignOut}
          className={`${ROW_CLASSES} border-none bg-transparent`}
          title={collapsed ? "Cerrar sesión" : undefined}
        >
          <i className="pi pi-sign-out" />
          <span className="app-sidebar__label font-bold">Cerrar sesión</span>
          <Ripple />
        </button>
      </div>
    </nav>
  );
};

export const useSidebarCollapsed = () => {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSED_KEY) === "1";
    } catch {
      return false;
    }
  });

  const toggle = useCallback(() => {
    setCollapsed((prev) => {
      try {
        localStorage.setItem(COLLAPSED_KEY, prev ? "0" : "1");
      } catch {
        /* modo privado o storage bloqueado: el estado vive solo en memoria */
      }
      return !prev;
    });
  }, []);

  return { collapsed, toggle };
};
